import React from 'react';

const Cancellation: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">Money Policy</h1>

          <div className="prose prose-lg max-w-none">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">SUBSCRIPTION TERMS</h2>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">1. Free or Paid Trial</h3>
            <p className="text-gray-600 mb-6">
              We may provide a free or low-cost trial period. If you don’t cancel before the trial ends, you’ll automatically be charged the regular subscription price displayed on the checkout screen.
              Trial details are always shown clearly before payment. In some cases, discounted trial plans such as $0.99, $2.99 or $6.99, £0.99, £2.99 or £6.99 may be offered and then renew at the standard rate afterward.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">2. Automatic Renewal</h3>
            <p className="text-gray-600 mb-6">
              Your subscription will renew automatically each month unless you cancel it in advance.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">3. Pricing Plan</h3>
            <p className="text-gray-600 mb-6">
              Watchi offers a monthly plan that includes a 3-day trial — which may be free or at a reduced cost (for example, $2.99 or $6.99).
              Once the trial period ends, you’ll be charged $29.99 or $39.99 per month, or £29.99 or £39.99 per month (depending on your country), and your plan will continue renewing automatically at that same rate.
              <br />
              If a payment fails, we may adjust your plan to a lower-priced tier.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">4. Payment Method</h3>
            <p className="text-gray-600 mb-6">
              Charges are made to the payment method you selected during checkout or at the end of your trial.
              By subscribing, you authorize Watchi to process all applicable fees using your chosen payment option.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">5. Cancellation</h3>
            <p className="text-gray-600 mb-6">
              Subscriptions renew automatically unless canceled at least 24 hours before the end of the trial or the current billing period.
              Please note that deleting the app does not cancel your subscription.
              <br />
              If you subscribed via our website, you can manage or cancel your plan anytime through your account settings.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">6. Refunds</h3>
            <p className="text-gray-600 mb-6">
              Refunds are processed in line with our refund policy and applicable laws.
              Once your subscription period ends, refunds are not available unless required by law.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">7. Price or Terms Changes</h3>
            <p className="text-gray-600 mb-6">
              We may update our pricing or terms from time to time.
              In such cases, we’ll provide reasonable notice through the app or by email.
              If you don’t agree with the new terms, you can cancel before they take effect.
            </p>

            <p className="text-gray-600 mb-8">
              For any questions, reach out to us via chat or email at{' '}
              <a href="mailto:support@watchi.app" className="text-blue-600 hover:text-blue-800">
                support@watchi.app
              </a>.
            </p>

            <hr className="my-8" />

            <h2 className="text-2xl font-semibold text-gray-800 mb-4">MONEY-BACK POLICY</h2>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">I. Money-Back Guarantee</h3>
            <p className="text-gray-600 mb-4">
              Besides your legal refund rights, if you purchased directly from our website and saw a money-back option at checkout,
              you may be eligible for a refund if you did not achieve visible results and meet all of the following conditions:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-600 mb-6">
              <li>Contact us within 30 days of your first purchase and before your current subscription period ends.</li>
              <li>
                Follow our program for:
                <ul className="list-disc pl-6 mt-2 space-y-1">
                  <li>at least 7 consecutive days within the first 30 days (for monthly or longer plans);</li>
                  <li>at least 5 consecutive days within the first 28 days (for 4-week plans);</li>
                  <li>at least 3 consecutive days within the first 7 days (for weekly or biweekly plans).</li>
                </ul>
              </li>
              <li>Provide proof that you followed the program as outlined in the “How to Demonstrate Program Compliance” section.</li>
            </ul>
            <p className="text-gray-600 mb-6">
              We’ll review your request and notify you if your refund has been approved.
            </p>
            <p className="text-gray-600 mb-8 font-semibold">
              Important: Only customers who meet all these requirements qualify for the Money-Back Guarantee.
              This policy applies exclusively in these cases and not to any other circumstances.
            </p>

            <hr className="my-8" />

            <h3 className="text-xl font-semibold text-gray-800 mb-3">II. General Refund Policy</h3>
            <p className="text-gray-600 mb-4">
              Our goal is to ensure that every Watchi user is satisfied with both the app and service.
              However, if you are not, you might be eligible for a refund under the conditions described below.
              Refunds may also be reviewed individually at our discretion.
            </p>
            <p className="text-gray-600 mb-4">
              Please note that refunds are generally available only during an active subscription period.
              If your plan has already expired, we won’t be able to process a refund.
            </p>
            <p className="text-gray-600 mb-8 font-semibold">
              Special Note (US Residents): If you live in California or Connecticut and cancel before midnight of the third business day after purchase, you are entitled to a full refund.
            </p>

            <hr className="my-8" />

            <h3 className="text-xl font-semibold text-gray-800 mb-3">For EU and EEA Residents</h3>
            <p className="text-gray-600 mb-4">
              If you reside in the European Economic Area or Switzerland, you have the right to withdraw from a service contract within 14 days of purchase —
              unless you have expressly agreed to immediate access to digital content (such as videos or PDFs), in which case your right of withdrawal no longer applies.
            </p>
            <p className="text-gray-600 mb-4">
              By subscribing to our ongoing Service, you agree to immediate access.
              If you cancel within 14 days, we will refund a proportional amount for the unused time.
            </p>
            <p className="text-gray-600 mb-4">
              To exercise your withdrawal right, contact us at{' '}
              <a href="mailto:support@watchi.app" className="text-blue-600 hover:text-blue-800">
                support@watchi.app
              </a>{' '}
              within 14 days of purchase and include a clear statement of your decision. You may also use this optional form:
            </p>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mt-6 mb-8">
              <h4 className="text-lg font-semibold text-gray-800 mb-4">Model Withdrawal Form</h4>
              <p className="text-gray-600 mb-2">
                To:{' '}
                <a href="mailto:support@watchi.app" className="text-blue-600 hover:text-blue-800">
                  support@watchi.app
                </a>
              </p>
            </div>

            <hr className="my-8" />

            <p className="text-gray-500 text-sm">Last Updated: December 1, 2024</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cancellation;